import { countries } from 'country-data-list'

/**
 * Region group definition for multi-select convenience.
 * Selecting a region toggles all its member countries.
 */
export interface RegionGroup {
  value: string
  label: string
  countries: string[] // ISO alpha-2 codes
}

/**
 * Country option for selection.
 */
export interface CountryOption {
  value: string // ISO alpha-2 code
  label: string // Country name
}

/**
 * Predefined region groups for quick multi-country selection.
 * Each region expands to its constituent ISO alpha-2 country codes.
 */
export const REGION_GROUPS: RegionGroup[] = [
  {
    value: 'europe',
    label: 'Europe',
    countries: [
      'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ',
      'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT',
      'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL',
      'MK', 'NO', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI',
      'ES', 'SE', 'CH', 'UA', 'GB', 'VA',
    ],
  },
  {
    value: 'eu',
    label: 'European Union',
    countries: [
      'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
      'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
      'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
    ],
  },
  {
    value: 'north_america',
    label: 'North America',
    countries: ['US', 'CA', 'MX'],
  },
  {
    value: 'south_america',
    label: 'South America',
    countries: [
      'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'GY', 'PY', 'PE', 'SR', 'UY', 'VE',
    ],
  },
  {
    value: 'middle_east',
    label: 'Middle East',
    countries: [
      'AE', 'BH', 'IL', 'IQ', 'IR', 'JO', 'KW', 'LB', 'OM', 'QA', 'SA', 'SY', 'YE',
    ],
  },
  {
    value: 'southeast_asia',
    label: 'Southeast Asia',
    countries: ['BN', 'KH', 'ID', 'LA', 'MY', 'MM', 'PH', 'SG', 'TH', 'VN'],
  },
  {
    value: 'east_asia',
    label: 'East Asia',
    countries: ['CN', 'JP', 'KR', 'TW', 'HK', 'MO', 'MN'],
  },
  {
    value: 'oceania',
    label: 'Oceania',
    countries: ['AU', 'NZ', 'FJ', 'PG', 'NC', 'VU', 'WS', 'TO'],
  },
  {
    value: 'africa',
    label: 'Africa',
    countries: [
      'DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CM', 'CV', 'CF', 'TD', 'KM', 'CG', 'CD',
      'CI', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'KE',
      'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG',
      'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG',
      'ZM', 'ZW',
    ],
  },
]

/**
 * Interface matching the country-data-list format.
 */
interface CountryData {
  alpha2: string
  alpha3: string
  name: string
  emoji?: string
  status: string
  ioc: string
}

/**
 * All countries from country-data-list, filtered to active ones.
 */
export const ALL_COUNTRIES: CountryOption[] = (countries.all as CountryData[])
  .filter((c) => c.emoji && c.status !== 'deleted' && c.ioc !== 'PRK')
  .map((c) => ({
    value: c.alpha2,
    label: c.name,
  }))
  .sort((a, b) => a.label.localeCompare(b.label))

/**
 * Map from ISO alpha-2 code to country name for fast lookup.
 */
const COUNTRY_LABEL_MAP: Record<string, string> = Object.fromEntries(
  ALL_COUNTRIES.map((c) => [c.value, c.label])
)

/**
 * Get the display label for a country code.
 */
export function getCountryLabel(code: string): string {
  return COUNTRY_LABEL_MAP[code.toUpperCase()] ?? code
}

/**
 * Get the display labels for multiple country codes.
 */
export function getCountryLabels(codes: string[]): string[] {
  return codes.map(getCountryLabel)
}

/**
 * Check if all countries in a region are selected.
 */
export function isRegionFullySelected(region: RegionGroup, selected: string[]): boolean {
  const selectedSet = new Set(selected.map((c) => c.toUpperCase()))
  return region.countries.every((c) => selectedSet.has(c))
}

/**
 * Check if any countries in a region are selected.
 */
export function isRegionPartiallySelected(region: RegionGroup, selected: string[]): boolean {
  const selectedSet = new Set(selected.map((c) => c.toUpperCase()))
  const hasAny = region.countries.some((c) => selectedSet.has(c))
  const hasAll = region.countries.every((c) => selectedSet.has(c))
  return hasAny && !hasAll
}

/**
 * Toggle a region: if fully selected, remove all; otherwise, add all.
 */
export function toggleRegion(region: RegionGroup, selected: string[]): string[] {
  const selectedSet = new Set(selected.map((c) => c.toUpperCase()))
  
  if (isRegionFullySelected(region, selected)) {
    // Remove all countries in the region
    return selected.filter((c) => !region.countries.includes(c.toUpperCase()))
  } else {
    // Add all missing countries from the region
    const toAdd = region.countries.filter((c) => !selectedSet.has(c))
    return [...selected, ...toAdd]
  }
}

/**
 * Expand geographic_focus values (region names like "europe") to ISO alpha-2 country codes.
 * Handles: region names, "global" (returns []), and pass-through of existing alpha2 codes.
 */
export function expandGeographicFocusToCountryCodes(
  values: string[] | null | undefined
): string[] {
  if (!values?.length) return []

  const seen = new Set<string>()
  const result: string[] = []

  const regionByValue: Record<string, string> = Object.fromEntries(
    REGION_GROUPS.flatMap((r) => [
      [r.value.toLowerCase(), r.value],
      [r.label.toLowerCase(), r.value],
    ])
  )

  const countryNameToCode: Record<string, string> = Object.fromEntries(
    (countries.all as CountryData[])
      .filter((c) => c.alpha2 && c.name)
      .map((c) => [c.name.toLowerCase(), c.alpha2])
  )

  for (const raw of values) {
    const v = raw.trim()
    if (!v) continue

    const lower = v.toLowerCase()

    if (lower === 'global' || lower === 'worldwide' || lower === 'international') continue

    if (/^[A-Za-z]{2}$/.test(v)) {
      const code = v.toUpperCase()
      if (!seen.has(code)) {
        seen.add(code)
        result.push(code)
      }
      continue
    }

    const regionValue = regionByValue[lower]
    if (regionValue) {
      const region = REGION_GROUPS.find((r) => r.value === regionValue)
      if (region) {
        for (const code of region.countries) {
          if (!seen.has(code)) {
            seen.add(code)
            result.push(code)
          }
        }
      }
      continue
    }

    const code = countryNameToCode[lower]
    if (code && !seen.has(code)) {
      seen.add(code)
      result.push(code)
    }
  }

  return result.sort()
}

/**
 * Find which regions are fully represented by the selected countries.
 * Used for smart badge collapsing.
 */
export function getFullySelectedRegions(selected: string[]): RegionGroup[] {
  return REGION_GROUPS.filter((r) => isRegionFullySelected(r, selected))
}

/**
 * Get country codes that are not part of any fully-selected region.
 * Used for showing individual country badges.
 */
export function getIndividualCountries(selected: string[]): string[] {
  const fullySelectedRegions = getFullySelectedRegions(selected)
  const regionCountries = new Set(fullySelectedRegions.flatMap((r) => r.countries))
  return selected.filter((c) => !regionCountries.has(c.toUpperCase()))
}
